/**
 * Chip — a small labelled token. Domain chips carry a colour dot; tag chips are
 * plain. Colour is never the only signal (doc 147 §8.8) — every chip has a label.
 *
 * When `onClick` is provided, renders as an interactive button with `aria-pressed`;
 * otherwise renders as a static span.
 */
export interface ChipProps {
  label: string;
  /** Domain colour token name, e.g. `d-investments` — renders a colour dot. */
  domainToken?: string;
  /** Semantic token name for the text colour, e.g. `danger`. */
  tone?: string;
  title?: string;
  /** When provided, renders as a clickable toggle button instead of a static span. */
  onClick?: () => void;
  /** Active/pressed state — only meaningful when onClick is provided. */
  selected?: boolean;
}

export function Chip({ label, domainToken, tone, title, onClick, selected }: ChipProps): React.JSX.Element {
  const baseClass = 'chip';
  const className = onClick && selected ? `${baseClass} is-active` : baseClass;

  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        title={title ?? label}
        aria-pressed={selected}
        onClick={onClick}
        style={tone ? { color: `var(--${tone})`, borderColor: `var(--${tone})` } : undefined}
      >
        {domainToken && (
          <span
            className="chip-dot"
            style={{ background: `var(--${domainToken})` }}
            aria-hidden="true"
          />
        )}
        {label}
      </button>
    );
  }

  return (
    <span
      className={baseClass}
      title={title ?? label}
      style={tone ? { color: `var(--${tone})`, borderColor: `var(--${tone})` } : undefined}
    >
      {domainToken && (
        <span
          className="chip-dot"
          style={{ background: `var(--${domainToken})` }}
          aria-hidden="true"
        />
      )}
      {label}
    </span>
  );
}
